// components/filePreview/renderers/XlsxRenderer.tsx
import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { Box, CircularProgress } from "@mui/material";

export const XlsxRenderer = ({ arrayBuffer }: { arrayBuffer: ArrayBuffer }) => {
  const [sheets, setSheets] = useState<string[]>([]);
  const [activeSheet, setActiveSheet] = useState("");
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [iframeContent, setIframeContent] = useState(""); // ✅ state-driven, no ref needed
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const wb = XLSX.read(arrayBuffer, { type: "array", cellStyles: true });
      setWorkbook(wb);
      setSheets(wb.SheetNames);
      setActiveSheet(wb.SheetNames[0]);
      renderSheet(wb, wb.SheetNames[0]);
    } catch (err) {
      console.error("XLSX parse error:", err);
    } finally {
      setLoading(false);
    }
  }, [arrayBuffer]);

  const renderSheet = (wb: XLSX.WorkBook, sheetName: string) => {
    const sheet = wb.Sheets[sheetName];

    if (!sheet) {
      setIframeContent("<html><body><p>No data found in this sheet.</p></body></html>");
      return;
    }

    const html = XLSX.utils.sheet_to_html(sheet);
    console.log("🔵 Sheet HTML length:", html.length); // verify data exists

    const styledHtml = `<!DOCTYPE html>
<html>
  <head>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body {
        font-family: Calibri, Arial, sans-serif;
        font-size: 13px;
        background: #fff;
        padding: 0;
        overflow: auto;
      }
      table {
        border-collapse: collapse;
        width: max-content;
        min-width: 100%;
      }
      td, th {
        border: 1px solid #d0d0d0;
        padding: 4px 10px;
        white-space: nowrap;
        vertical-align: middle;
        min-width: 80px;
      }
      tr:first-child td {
        background-color: #f2f2f2;
        font-weight: 600;
        position: sticky;
        top: 0;
        z-index: 1;
        border-bottom: 2px solid #bbb;
      }
      tr:nth-child(even) td { background-color: #fafafa; }
      tr:hover td { background-color: #eef4ff; }
    </style>
  </head>
  <body>${html}</body>
</html>`;

    setIframeContent(styledHtml); // ✅ srcdoc update triggers re-render
  };

  const handleSheetChange = (sheetName: string) => {
    setActiveSheet(sheetName);
    if (workbook) renderSheet(workbook, sheetName);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "65vh" }}>

      {/* Sheet tabs */}
      {sheets.length > 1 && (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 0.5 }}>
          {sheets.map((sheet) => (
            <Box
              key={sheet}
              onClick={() => handleSheetChange(sheet)}
              sx={{
                px: 2, py: 0.5,
                fontSize: 12,
                cursor: "pointer",
                borderRadius: "4px 4px 0 0",
                border: "1px solid",
                borderColor: activeSheet === sheet ? "primary.main" : "divider",
                backgroundColor: activeSheet === sheet ? "primary.50" : "grey.100",
                color: activeSheet === sheet ? "primary.main" : "text.secondary",
                fontWeight: activeSheet === sheet ? 600 : 400,
                "&:hover": { backgroundColor: "grey.200" },
              }}
            >
              {sheet}
            </Box>
          ))}
        </Box>
      )}

      {/* ✅ srcdoc — React controls content, no DOM manipulation needed */}
      <Box sx={{ flex: 1, border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
        <iframe
          srcDoc={iframeContent}
          width="100%"
          height="100%"
          style={{ border: "none" }}
          title="Excel Preview"
          sandbox="allow-same-origin" // ✅ allows iframe to read its own srcdoc content
        />
      </Box>

    </Box>
  );
};